import {
  Flex,
  FormLabel,
  Input,
  Tag,
  TagLabel,
  TagLeftIcon,
  Tooltip,
} from '@chakra-ui/react';
import { DateTime } from 'luxon';
import React from 'react';
import { FieldPath, useFormContext, useWatch } from 'react-hook-form';

import { typeguards } from '@finley-technologies/common';

import { Icon } from 'packages/common-frontend/components/icons';
import {
  DateInput as DateInputBase,
  DateInputProps,
} from 'packages/common-frontend/components/InputsBETA/DateInput';
import {
  InputCard as InputCardBase,
  InputCardProps,
} from 'packages/common-frontend/components/InputsBETA/InputCard';
import {
  SelectInput as SelectInputBase,
  SelectInputProps,
} from 'packages/common-frontend/components/InputsBETA/SelectInput';
import {
  TextInput as TextInputBase,
  TextInputProps,
} from 'packages/common-frontend/components/InputsBETA/TextInput';
import { useCreditAgreementStore } from '~/core/routes/settings/implementations/common/CreditAgreementProvider/CreditAgreementProvider';
import {
  computeDiff,
  DiffProps,
} from '~/core/routes/settings/implementations/common/DiffInputs/hooks';
import { StrictFieldValues } from '~/core/routes/settings/implementations/common/Inputs/types';
import { DiffableFormData } from '~/core/routes/settings/implementations/common/types';

type PlaceholderInputProps = {
  label?: string;
  tag?: React.ReactNode;
  value: string | undefined;
};
export const PlaceholderInput = ({
  label,
  tag,
  value,
}: PlaceholderInputProps) => (
  <Flex direction="column" width="100%">
    <Flex alignItems="center">
      {label && <FormLabel fontWeight="bold">{label}</FormLabel>}
      {tag}
    </Flex>
    <Input
      role="presentation"
      readOnly
      tabIndex={-1}
      _focus={{ boxShadow: 'none', borderColor: 'inherit' }}
      cursor="default"
      value={value || ''}
    />
  </Flex>
);

const diffColorMap: Record<DiffProps['status'], string> = {
  deleted: 'red.500',
  added: 'green.500',
  modified: 'green.500',
};

const TAG_CLASSNAME = 'implementation-portal-diff-tag';

export function DiffTag({
  diff,
  disableTooltip,
}: {
  diff: DiffProps | undefined | null;
  disableTooltip?: boolean;
}) {
  if (!diff) return null;

  switch (diff.status) {
    case 'added':
      return (
        <Tag
          variant="solid-muted"
          colorScheme="green"
          size="sm"
          mb={2}
          className={TAG_CLASSNAME}
        >
          <TagLeftIcon>
            <Icon icon="plus" size="md" />
          </TagLeftIcon>
          <TagLabel>Added</TagLabel>
        </Tag>
      );

    case 'deleted':
      return (
        <Tag
          variant="solid-muted"
          colorScheme="red"
          size="sm"
          mb={2}
          className={TAG_CLASSNAME}
        >
          <TagLeftIcon>
            <Icon icon="minus" size="md" />
          </TagLeftIcon>
          <TagLabel>Deleted</TagLabel>
        </Tag>
      );

    case 'modified':
      return (
        <Tooltip
          label={diff.tooltip ?? ''}
          isDisabled={disableTooltip || !diff.tooltip}
        >
          <Tag
            variant="solid-muted"
            colorScheme="blue"
            size="sm"
            mb={2}
            className={TAG_CLASSNAME}
          >
            <TagLeftIcon>
              <Icon icon="pencil" size="md" />
            </TagLeftIcon>
            <TagLabel>Modified</TagLabel>
          </Tag>
        </Tooltip>
      );

    default:
      return null;
  }
}

type ExtendedTextInputProps<
  T extends DiffableFormData<StrictFieldValues>
> = TextInputProps<T> & {
  diffName: TextInputProps<T>['name'] & `diffForm.${string}`;
};

export const TextInput = <T extends DiffableFormData<StrictFieldValues>>(
  props: ExtendedTextInputProps<T>,
) => {
  const isEditing = useCreditAgreementStore((store) => store.isEditing);

  const form = useFormContext<T>();
  const value = useWatch<T>({ control: form.control, name: props.name });
  const diffValue = useWatch<T>({
    control: form.control,
    name: props.diffName,
  });

  const diff = computeDiff(value, diffValue);

  const shouldDisplayDiff = diff && !isEditing;
  const tag = shouldDisplayDiff ? <DiffTag diff={diff} /> : undefined;

  if (!isEditing) {
    return (
      <PlaceholderInput
        value={String(value || diff?.previousValue)}
        label={props.label}
        tag={tag}
      />
    );
  }

  return (
    <TextInputBase<T>
      {...props}
      tag={tag}
      placeholder={shouldDisplayDiff ? diff?.previousValue : props.placeholder}
    />
  );
};

type ExtendedSelectInputProps<
  T extends DiffableFormData<StrictFieldValues>
> = SelectInputProps<T> & {
  diffName: SelectInputProps<T>['name'] & `diffForm.${string}`;
};

export const SelectInput = <T extends DiffableFormData<StrictFieldValues>>(
  props: ExtendedSelectInputProps<T>,
) => {
  const isEditing = useCreditAgreementStore((store) => store.isEditing);

  const form = useFormContext<T>();
  const value = useWatch({ control: form.control, name: props.name });
  const diffValue = useWatch({
    control: form.control,
    name: props.diffName,
  });

  const diff = computeDiff(value, diffValue);

  const shouldDisplayDiff = diff && !isEditing;
  const tag = shouldDisplayDiff ? <DiffTag diff={diff} /> : undefined;

  if (!isEditing) {
    return (
      <PlaceholderInput
        value={String(value || diff?.previousValue)}
        label={props.label}
        tag={tag}
      />
    );
  }

  return (
    <SelectInputBase<T>
      {...props}
      tag={tag}
      placeholder={shouldDisplayDiff ? diff?.previousValue : props.placeholder}
    />
  );
};

const dateFormatter = (fullDateStr: unknown) => {
  if (typeguards.isFullDateStr(fullDateStr)) {
    return DateTime.fromISO(fullDateStr).toLocaleString(DateTime.DATE_MED);
  }
  return 'None';
};

type ExtendedDateInputProps<
  T extends DiffableFormData<StrictFieldValues>
> = DateInputProps<T> & {
  diffName: DateInputProps<T>['name'] & `diffForm.${string}`;
};

export const DateInput = <T extends DiffableFormData<StrictFieldValues>>(
  props: ExtendedDateInputProps<T>,
) => {
  const isEditing = useCreditAgreementStore((store) => store.isEditing);

  const form = useFormContext<T>();

  const value = useWatch({ control: form.control, name: props.name });
  const diffValue = useWatch({
    control: form.control,
    name: props.diffName,
  });

  const diff = computeDiff(value, diffValue, (a, b) => a === b, dateFormatter);

  const shouldDisplayDiff = diff && !isEditing;
  const tag = shouldDisplayDiff ? <DiffTag diff={diff} /> : undefined;

  if (!isEditing) {
    const formattedValue = dateFormatter(String(value));

    return (
      <PlaceholderInput
        value={value ? formattedValue : diff?.previousValue}
        label={props.label}
        tag={tag}
      />
    );
  }

  return (
    <DateInputBase<T>
      {...props}
      tag={tag}
      placeholder={shouldDisplayDiff ? diff?.previousValue : props.placeholder}
    />
  );
};

type InputCardPropsWithDiff = InputCardProps & {
  diff: DiffProps | undefined;
};
export const InputCardWithDiff = (props: InputCardPropsWithDiff) => {
  const diff = props.diff;
  const isEditing = useCreditAgreementStore((s) => s.isEditing);

  const shouldDisplayDiff = diff && !isEditing && diff.status !== 'modified'; // Only show tag for 'added' or 'deleted'
  const tag = shouldDisplayDiff ? <DiffTag diff={diff} /> : undefined;

  // Don't render deleted fields when not in editing mode
  if (diff?.status === 'deleted') return null;

  return (
    <InputCardBase
      {...props}
      deleteProperty={isEditing ? props.deleteProperty : undefined}
      tag={tag}
      CardProps={{
        borderColor: shouldDisplayDiff ? diffColorMap[diff.status] : undefined,
        borderWidth: shouldDisplayDiff ? '2px' : undefined,
        sx: shouldDisplayDiff
          ? {
              [`.${TAG_CLASSNAME}`]: {
                // If an InputContainer is showing that it has been added or removed,
                // then hide all diff tags in child components
                display: 'none',
              },
            }
          : undefined,
      }}
    />
  );
};

type InputCardPropsWithName<T extends StrictFieldValues> = InputCardProps & {
  name: FieldPath<T>;
  diffName: FieldPath<T>;
};

export const InputCard = <T extends StrictFieldValues>(
  props: InputCardPropsWithName<T>,
) => {
  const form = useFormContext<T>();
  const value = useWatch<T>({ control: form.control, name: props.name });
  const diffValue = useWatch<T>({
    control: form.control,
    name: props.diffName,
  });

  const diff = computeDiff(value, diffValue);

  return <InputCardWithDiff diff={diff} {...props} />;
};

export const WholePageDiffOutline = ({
  diff,
  children,
}: {
  diff: DiffProps | undefined;
  children: React.ReactNode;
}) => {
  const isEditing = useCreditAgreementStore((s) => s.isEditing);

  const shouldDisplayDiff = diff && !isEditing && diff.status !== 'modified'; // Only show tag for 'added' or 'deleted'

  const borderStyle =
    diff && shouldDisplayDiff
      ? {
          borderColor: diffColorMap[diff.status],
          borderWidth: '2px',
          borderRadius: '6px',
        }
      : {};

  return (
    <Flex direction="column">
      <Flex marginLeft="1rem">
        {shouldDisplayDiff && <DiffTag diff={diff} />}
      </Flex>
      <Flex
        direction="column"
        m="1rem"
        p="0.5rem"
        {...borderStyle}
        sx={
          shouldDisplayDiff
            ? {
                [`.${TAG_CLASSNAME}`]: {
                  // If a WholePageDiffOutline is showing that it has been added or removed,
                  // then hide all diff tags in child components
                  display: 'none',
                },
              }
            : undefined
        }
      >
        {children}
      </Flex>
    </Flex>
  );
};

export const ShowWhileEditing = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const isEditing = useCreditAgreementStore((store) => store.isEditing);

  return isEditing ? children : null;
};

export const ShowWhileDiffing = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const isEditing = useCreditAgreementStore((store) => store.isEditing);

  return !isEditing ? children : null;
};
